import { useEffect, useState } from 'react';
import { FileSearch, Loader2, Search } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { EmptyState } from '@/components/ui/empty-state';
import { ListSkeleton } from '@/components/ui/skeleton';
import { cn } from '@/lib/utils';
import { useSalesOrderStore } from '../store/salesOrderStore';
import type { FirmOfferSummaryDto } from '../types/salesOrder.types';

const RECENT_TAKE = 15;
const SEARCH_TAKE = 25;

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called when the user picks an FO. The caller decides what to do (create a
   *  draft seeded with it, or change an existing draft's FO). */
  onPick: (fo: FirmOfferSummaryDto) => void;
}

// Creation-time Firm Offer picker. Defaults to "My FOs" (server scopes to the
// signed-in coordinator), with an "All" toggle and a combined search over
// FO# / project / customer / coordinator / rep. Reuses the store search +
// shared Skeleton/EmptyState primitives.
export function FoPickerModal({ open, onOpenChange, onPick }: Props) {
  const [query, setQuery] = useState('');
  const [mine, setMine] = useState(true);
  // Flips true synchronously the moment a search is queued, so the loading state
  // renders on the very first frame the dialog opens — no flash of stale/empty
  // content before the request kicks in.
  const [loadingResults, setLoadingResults] = useState(true);
  const results = useSalesOrderStore((s) => s.foSearchResults);
  // Store flag — set atomically with foSearchResults in a single set(), so it
  // clears in the SAME render the results land. Gating on it (not just the local
  // finally-driven flag) prevents an empty-state flash between loading and list.
  const isSearching = useSalesOrderStore((s) => s.isSearchingFo);
  const searchFirmOffers = useSalesOrderStore((s) => s.searchFirmOffers);

  // Reset to the default view and clear stale rows each time the modal opens, so
  // the first frame is a clean loading state rather than the previous results.
  useEffect(() => {
    if (open) {
      setQuery('');
      setMine(true);
      setLoadingResults(true);
      useSalesOrderStore.setState({ foSearchResults: [] });
    }
  }, [open]);

  // Combined search. Fires immediately on open / My-All toggle (no query) and
  // debounced while typing. `loadingResults` covers the whole window so the UI
  // never flickers between empty and loading.
  useEffect(() => {
    if (!open) return;
    setLoadingResults(true);
    const trimmed = query.trim();
    const handle = setTimeout(() => {
      searchFirmOffers(trimmed || null, null, trimmed ? SEARCH_TAKE : RECENT_TAKE, mine)
        .finally(() => setLoadingResults(false));
    }, trimmed ? 250 : 0);
    return () => clearTimeout(handle);
  }, [open, query, mine, searchFirmOffers]);

  const hasQuery = query.trim().length > 0;
  // Show the loading state whenever a search is queued (local, covers the debounce
  // gap) OR in flight (store, atomic with results) and there's nothing to show yet.
  // Because results + isSearching update together, the list replaces loading with
  // no empty-state flash in between.
  const showLoading = (loadingResults || isSearching) && results.length === 0;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl">
        <DialogHeader>
          <DialogTitle>Select a Firm Offer</DialogTitle>
        </DialogHeader>

        {/* My / All toggle */}
        <div className="inline-flex w-fit rounded-md border p-0.5 text-sm">
          <button
            type="button"
            onClick={() => setMine(true)}
            className={cn(
              'rounded px-3 py-1 font-medium transition-colors',
              mine ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            My FOs
          </button>
          <button
            type="button"
            onClick={() => setMine(false)}
            className={cn(
              'rounded px-3 py-1 font-medium transition-colors',
              !mine ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            All
          </button>
        </div>

        {/* Combined search */}
        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search FO# / project / customer / coordinator / rep…"
            className="h-9 pl-9 pr-9"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
          />
          {loadingResults && (
            <span aria-hidden className="pointer-events-none absolute right-3 top-1/2 -translate-y-1/2">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </span>
          )}
        </div>

        {/* Results */}
        <div className="max-h-[50vh] min-h-[16rem] overflow-y-auto rounded-md border">
          {showLoading ? (
            <ListSkeleton rows={6} className="p-3" />
          ) : results.length === 0 ? (
            <EmptyState
              icon={FileSearch}
              title={mine ? 'No Firm Offers assigned to you' : 'No eligible Firm Offers found'}
              description={
                mine
                  ? 'Only awarded FOs with a positive balance appear here. Try viewing all.'
                  : hasQuery
                    ? 'No awarded FOs with a positive balance match your search.'
                    : 'No eligible Firm Offers available.'
              }
              action={
                mine ? (
                  <Button variant="outline" size="sm" onClick={() => setMine(false)}>
                    View all
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <div className="divide-y">
              {results.map((fo) => (
                <button
                  key={fo.internalId}
                  type="button"
                  onClick={() => onPick(fo)}
                  className="flex w-full items-center justify-between gap-3 px-3 py-2 text-left hover:bg-muted/50"
                >
                  <div className="min-w-0 flex-1">
                    <div className="truncate font-mono text-xs font-semibold">{fo.tranId}</div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {fo.customerName}
                      {fo.projectName ? ` · ${fo.projectName}` : ''}
                    </div>
                    <div className="truncate text-[11px] text-muted-foreground">
                      {fo.salesCoordinator?.refName ?? '—'}
                      {fo.salesRep?.refName ? ` · Rep: ${fo.salesRep.refName}` : ''}
                    </div>
                  </div>
                  <div className="shrink-0 whitespace-nowrap text-right text-[11px] tabular-nums">
                    <div className="font-semibold text-success">{fo.balanceKg.toFixed(2)} kg</div>
                    <div className="text-muted-foreground">of {fo.totalAuthorizedKg.toFixed(2)}</div>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
